import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import ShieldOutlined from "@mui/icons-material/ShieldOutlined";
import { io } from "socket.io-client";
import identityService from "../../api/identityService";
import techniciansService from "../../api/techniciansService";
import LiveActivity from "./LiveActivity";
import { API_CONFIG } from "../../utils/constants";
import { ROLE_LABELS } from "../../components/navConfig";

const ROLES = [
  { value: "admin", label: "Administrador" },
  { value: "rh", label: "Recursos humanos" },
  { value: "scheduler", label: "Programación" },
  { value: "supervisor", label: "Supervisor" },
  { value: "calidad", label: "Calidad" },
  { value: "technician", label: "Técnico" },
  { value: "costumer", label: "Cliente" },
];

const KINDS = [
  { value: "", label: "Todos" },
  { value: "issued", label: "Emitidos" },
  { value: "refreshed", label: "Renovados" },
  { value: "used", label: "Uso" },
  { value: "rejected", label: "Rechazados" },
  { value: "revoked", label: "Revocados" },
];

const emptyForm = { name: "", email: "", password: "", role: "scheduler", status: "active", technician: "" };

function messageOf(err) {
  return err?.response?.data?.message || err?.message || "No se pudo completar la operación";
}

function when(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString("es-MX", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function Seguridad() {
  const [tab, setTab] = useState(0);
  const [overview, setOverview] = useState(null);
  const [center, setCenter] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [active, setActive] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [kind, setKind] = useState("");
  const [error, setError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [technicians, setTechnicians] = useState([]);

  useEffect(() => {
    techniciansService.getAll()
      .then((res) => setTechnicians(res.data?.data || res.data || []))
      .catch(() => setTechnicians([]));
  }, []);

  const load = useCallback(async () => {
    try {
      const [overviewRes, centerRes, tokenRes, activeRes, sessionRes, accountRes] = await Promise.all([
        identityService.overview(),
        identityService.authCenter(),
        identityService.tokens({ kind: kind || undefined, limit: 80 }),
        identityService.activeTokens(),
        identityService.sessions(),
        identityService.accounts(),
      ]);
      setOverview(overviewRes.data.data);
      setCenter(centerRes.data.data);
      setTokens(tokenRes.data.data?.events || []);
      setActive(activeRes.data.data || []);
      setSessions(sessionRes.data.data || []);
      setAccounts(accountRes.data.data || []);
      setError("");
    } catch (err) {
      setError(messageOf(err));
    }
  }, [kind]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return undefined;
    const socket = io(`${API_CONFIG.SOCKET_URL}/presence`, {
      auth: { token },
      transports: ["websocket", "polling"],
    });
    const refresh = () => load();
    socket.on("monitor:refresh", refresh);
    return () => {
      socket.off("monitor:refresh", refresh);
      socket.disconnect();
    };
  }, [load]);

  const saveAccount = async () => {
    setSaving(true);
    const payload = { name: form.name, role: form.role, status: form.status };
    if (form.role === "technician") payload.technician = form.technician;
    if (form.password) payload.password = form.password;
    try {
      if (editing) await identityService.updateAccount(editing.id || editing._id, payload);
      else await identityService.createAccount({ ...payload, email: form.email, password: form.password });
      setFormOpen(false);
      await load();
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setSaving(false);
    }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setSaving(true);
    try {
      await confirm.run();
      setConfirm(null);
      await load();
    } catch (err) {
      setError(messageOf(err));
      setConfirm(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ShieldOutlined />
            <Typography variant="h5" sx={{ fontWeight: 800 }}>Seguridad</Typography>
          </Box>
          <Typography color="text.secondary">
            Sesiones de 8 horas, token de 15 minutos, una sesión por cuenta y registro de cada acceso.
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => { setEditing(null); setForm(emptyForm); setFormOpen(true); }}>
          Nueva cuenta
        </Button>
      </Box>

      {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}

      <Tabs value={tab} onChange={(_, value) => setTab(value)}>
        <Tab label="En vivo" />
        <Tab label="Resumen" />
        <Tab label="Accesos" />
        <Tab label={`Tokens${active.length ? ` · ${active.length}` : ""}`} />
        <Tab label={`Sesiones${sessions.length ? ` · ${sessions.length}` : ""}`} />
        <Tab label="Cuentas" />
      </Tabs>

      {tab === 0 && <LiveActivity />}

      {tab === 1 && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1.5 }}>
          {[
            ["Tokens vigentes", overview?.activeTokensEstimate ?? 0],
            ["Emitidos 24 h", overview?.last24h?.issued || 0],
            ["Rechazados 24 h", overview?.last24h?.rejected || 0],
            ["Sesiones abiertas", overview?.activeSessions ?? 0],
            ["Suspendidas", overview?.suspended || 0],
            ["Revocados 24 h", overview?.revoked24h || 0],
          ].map(([label, value]) => (
            <Box key={label} sx={{ p: 2, border: "1px solid #e5e7eb", borderRadius: 2, bgcolor: "#fff" }}>
              <Typography variant="body2" color="text.secondary">{label}</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>{value}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {tab === 2 && (
        <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 2, bgcolor: "#fff" }}>
          <Box sx={{ display: "flex", gap: 2, p: 2, flexWrap: "wrap" }}>
            {Object.entries(center?.counts || {}).map(([key, count]) => (
              <Chip key={key} label={`${key}: ${count}`} />
            ))}
          </Box>
          {(center?.events || []).map((event) => (
            <Box key={event.id} sx={{ px: 2, py: 1, borderTop: "1px solid #f3f4f6" }}>
              <Typography sx={{ fontWeight: 700 }}>{event.kind} · {event.userName || event.email || "—"}</Typography>
              <Typography variant="body2" color="text.secondary">
                {ROLE_LABELS[event.role] || event.role || "—"} · {event.ip || "—"} · {when(event.at)}
                {event.reason ? ` · ${event.reason}` : ""}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {tab === 3 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {KINDS.map((item) => (
              <Chip key={item.value || "all"} label={item.label} color={kind === item.value ? "primary" : "default"} onClick={() => setKind(item.value)} />
            ))}
          </Box>
          {active.map((event) => (
            <Box key={event.jti} sx={{ p: 1.5, border: "1px solid #e5e7eb", borderRadius: 2, bgcolor: "#fff", display: "flex", justifyContent: "space-between" }}>
              <Box>
                <Typography sx={{ fontWeight: 700 }}>{event.userName || "Sin nombre"}</Typography>
                <Typography variant="body2" color="text.secondary">{event.tokenType} · {event.client} · {event.ipAddress}</Typography>
              </Box>
              <Button onClick={() => setConfirm({
                title: "Revocar token",
                body: "Esta credencial deja de funcionar de inmediato.",
                run: () => identityService.revokeToken(event.jti, "revocado desde el monitor"),
              })}>Revocar</Button>
            </Box>
          ))}
          {tokens.map((event) => (
            <Typography key={event._id} variant="body2">
              {when(event.createdAt)} · {event.userName || "—"} · {event.kind} · {event.tokenType} · {event.ipAddress || "—"}
            </Typography>
          ))}
        </Box>
      )}

      {tab === 4 && sessions.map((session) => (
        <Box key={session.id} sx={{ p: 1.5, border: "1px solid #e5e7eb", borderRadius: 2, bgcolor: "#fff", display: "flex", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontWeight: 700 }}>{session.userName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {session.userEmail} · {when(session.loginTime)} · {session.ipAddress || "—"}
            </Typography>
          </Box>
          <Button onClick={() => setConfirm({
            title: "Cerrar sesión",
            body: `Se cierra la sesión de ${session.userName} y se invalidan sus tokens.`,
            run: () => identityService.endSession(session.id, "sesión cerrada desde el monitor"),
          })}>Cerrar</Button>
        </Box>
      ))}

      {tab === 5 && accounts.map((account) => {
        const id = account.id || account._id;
        return (
          <Box key={id} sx={{ p: 1.5, border: "1px solid #e5e7eb", borderRadius: 2, bgcolor: "#fff", display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
            <Box>
              <Typography sx={{ fontWeight: 700 }}>{account.name} {account.present ? "· en línea" : ""}</Typography>
              <Typography variant="body2" color="text.secondary">
                {account.email} · {ROLE_LABELS[account.role] || account.role}
                {account.role === "technician" && account.technicianName ? ` · ${account.technicianName}` : ""}
                {" · "}{account.status === "suspended" ? "Suspendida" : "Activa"} · {when(account.lastLogin)}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button onClick={() => {
                setEditing(account);
                setForm({
                  name: account.name,
                  email: account.email,
                  password: "",
                  role: account.role,
                  status: account.status || "active",
                  technician: account.technician || "",
                });
                setFormOpen(true);
              }}>Editar</Button>
              <Button onClick={() => setConfirm({
                title: "Revocar tokens",
                body: `Todos los tokens de ${account.name} dejan de servir.`,
                run: () => identityService.revokeAccount(id, "revocación global desde el monitor"),
              })}>Revocar</Button>
              <Button onClick={() => setConfirm({
                title: account.status === "suspended" ? "Reactivar" : "Suspender",
                body: account.status === "suspended" ? "La cuenta podrá entrar de nuevo." : "La cuenta no podrá autenticarse.",
                run: () => identityService.updateAccount(id, { status: account.status === "suspended" ? "active" : "suspended" }),
              })}>{account.status === "suspended" ? "Activar" : "Suspender"}</Button>
            </Box>
          </Box>
        );
      })}

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? "Editar cuenta" : "Nueva cuenta"}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: "8px !important" }}>
          <TextField label="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <TextField label="Correo" value={form.email} disabled={Boolean(editing)} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <TextField label={editing ? "Nueva contraseña" : "Contraseña"} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          <TextField select label="Rol" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            {ROLES.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
          </TextField>
          {form.role === "technician" && (
            <TextField
              select
              label="Técnico del sistema"
              value={form.technician}
              onChange={(e) => setForm({ ...form, technician: e.target.value })}
              helperText="La cuenta solo verá la agenda de este técnico"
            >
              {technicians.map((tech) => (
                <MenuItem key={tech._id} value={tech._id}>{tech.name}</MenuItem>
              ))}
            </TextField>
          )}
          {editing && (
            <TextField select label="Estado" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <MenuItem value="active">Activa</MenuItem>
              <MenuItem value="suspended">Suspendida</MenuItem>
            </TextField>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancelar</Button>
          <Button variant="contained" disabled={saving} onClick={saveAccount}>Guardar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirm)} onClose={() => setConfirm(null)}>
        <DialogTitle>{confirm?.title}</DialogTitle>
        <DialogContent><Typography>{confirm?.body}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancelar</Button>
          <Button variant="contained" disabled={saving} onClick={runConfirm}>Confirmar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
